/**
 * Badge.tsx — shared pill. Tone classes are full static literals so Tailwind's
 * scanner keeps them. Colour is always paired with a text label (WCAG 1.4.1);
 * `accent` is the only gold tone.
 */

import type { HTMLAttributes } from 'react';

export type BadgeTone = 'ok' | 'degraded' | 'down' | 'unknown' | 'neutral' | 'accent';

const TONE_CLASSES: Record<BadgeTone, string> = {
  ok: 'bg-status-ok/15 text-status-ok ring-status-ok/40',
  degraded: 'bg-status-degraded/15 text-status-degraded ring-status-degraded/40',
  down: 'bg-status-down/15 text-status-down ring-status-down/40',
  unknown: 'bg-status-unknown/15 text-status-unknown ring-status-unknown/40',
  neutral: 'bg-surface-3 text-ink-2 ring-ink-3/30',
  accent: 'bg-gold/15 text-gold ring-gold/40',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone: BadgeTone;
  /** Hollow dot (e.g. "inactive") instead of the solid one. */
  hollow?: boolean;
}

export const Badge = ({ tone, hollow = false, className = '', children, ...rest }: BadgeProps) => (
  <span
    // eslint-disable-next-line react/jsx-props-no-spreading -- primitive forwards native HTML attributes (data-testid, aria-*) to the root element
    {...rest}
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-meta font-medium ring-1 ring-inset ${TONE_CLASSES[tone]} ${className}`.trim()}
  >
    <span
      aria-hidden="true"
      className={`h-1.5 w-1.5 rounded-full ${hollow ? 'border border-current' : 'bg-current'}`}
    />
    {children}
  </span>
);
